/**
 * STEP ELEVEN — "Now it all comes together."
 *
 * The capstone level. All six organs in the pool, only three wall ports
 * (gruel spout, atmosphere bidirectional, toilet). No external blood
 * spouts — the heart is what kicks the whole cascade alive (its A-out
 * always emits CO₂). The student wires every organ into a single closed
 * blood loop.
 *
 * Intended solution (every cone wired):
 *   gruel (TOP) → stomach.in → stomach.out (food) → intestines.top (direct link)
 *   atmosphere (TOP) ↔ lungs.top (trachea)
 *   diaphragm bottom-castellated to lungs.bottom (direct link)
 *   heart.A-out (always-on CO₂) → lungs.left-in
 *   lungs.right-out (O₂) → heart.B-in
 *   heart.B-out (O₂) → intestines.left-in
 *   intestines.left-out (food+O₂+CO₂) → brain.A-in
 *   brain.A-out (CO₂) → heart.A-in   ← closes the loop
 *   intestines.bottom (waste) → toilet (BOTTOM)
 *
 * Brain's left-in accepts BOTH the pure [food,oxygen] mix AND the
 * intestines' enriched [food,oxygen,co2] mix via acceptsAlso — pedagogically
 * the brain only consumes food + O₂, but real body blood carries CO₂ too.
 */
import {
  toiletReceptacle,
  topAtmosphere,
  topSpout,
  type LevelConfig,
} from './portHelpers';
import type { MoleculeMix } from '../types';

export const LEVEL_11: LevelConfig = {
  step: 11,
  title: 'STEP ELEVEN',
  instructions: 'Now it all comes together…',
  // Trachea (atmosphere ↔ lungs top) is mandatory — without it the
  // bidirectional atmosphere could be piped straight into an O₂ receptacle,
  // bypassing the lungs. See LevelConfig.requiredLinks.
  requiredLinks: [{ a: 's11-atmos', b: 's11-lungs-top' }],
  ports: [
    // TOP wall — gruel + atmosphere only.
    topSpout('s11-gruel-in', 7, ['gruel']),
    topAtmosphere('s11-atmos', 12, ['oxygen']),
    // BOTTOM wall — toilet.
    toiletReceptacle('s11-toilet', 17),
  ],
  organPool: [
    // ---------- Heart (copy of Level 7 heart, s11- prefix) ----------
    {
      id: 'heart',
      art: 'heart',
      cols: 4,
      rows: 4,
      activatesWhen: ['s11-heart-A-out', 's11-heart-B-out'],
      ports: [
        {
          id: 's11-heart-A-in',
          kind: 'receptacle',
          facing: 'N',
          cellOffset: { dCol: 0, dRow: -1 },
          carries: ['co2'],
        },
        {
          id: 's11-heart-A-out',
          kind: 'spout',
          facing: 'S',
          cellOffset: { dCol: 0, dRow: 3 },
          carries: ['co2'],
        },
        {
          id: 's11-heart-B-in',
          kind: 'receptacle',
          facing: 'N',
          cellOffset: { dCol: 3, dRow: -1 },
          carries: ['oxygen'],
        },
        {
          id: 's11-heart-B-out',
          kind: 'spout',
          facing: 'S',
          cellOffset: { dCol: 3, dRow: 3 },
          carries: ['oxygen'],
          dependsOn: ['s11-heart-B-in'],
        },
      ],
    },
    // ---------- Lungs (copy of Level 5/6 lungs, s11- prefix) ----------
    {
      id: 'lungs',
      art: 'lungs',
      cols: 5,
      rows: 3,
      activatesWhen: ['s11-lungs-right-out'],
      ports: [
        {
          id: 's11-lungs-top',
          kind: 'bidirectional',
          facing: 'N',
          cellOffset: { dCol: 2, dRow: -1 },
          carries: ['oxygen'],
          coneFlipped: true,
          transform: (inputs) => {
            const left = inputs.get('s11-lungs-left-in') ?? [];
            const bottom = inputs.get('s11-lungs-bottom-pulse') ?? [];
            const operating = bottom.includes('pulse');
            const tracheaInputArriving = (inputs.get('s11-lungs-top') ?? []).length > 0;
            if (operating && tracheaInputArriving && left.includes('co2')) {
              return ['co2'];
            }
            return null;
          },
        },
        {
          id: 's11-lungs-left-in',
          kind: 'receptacle',
          facing: 'W',
          cellOffset: { dCol: -1, dRow: 0 },
          carries: ['co2'],
          acceptsAny: true,
          validIf: (inputs) => {
            const bottom = inputs.get('s11-lungs-bottom-pulse') ?? [];
            return bottom.includes('pulse');
          },
        },
        {
          id: 's11-lungs-right-out',
          kind: 'spout',
          facing: 'E',
          cellOffset: { dCol: 5, dRow: 0 },
          carries: ['oxygen'],
          transform: (inputs) => {
            const left = inputs.get('s11-lungs-left-in') ?? [];
            const bottom = inputs.get('s11-lungs-bottom-pulse') ?? [];
            const operating = bottom.includes('pulse');
            if (!operating) return null;
            if (left.length === 0) return null;
            const tracheaConnected = (inputs.get('s11-lungs-top') ?? []).length > 0;
            if (!tracheaConnected) {
              return left as MoleculeMix;
            }
            const out = new Set(left.filter((m) => m !== 'co2'));
            out.add('oxygen');
            return Array.from(out);
          },
        },
        {
          id: 's11-lungs-bottom-pulse',
          kind: 'receptacle',
          facing: 'S',
          cellOffset: { dCol: 2, dRow: 3 },
          carries: ['pulse'],
          shape: 'castellated',
        },
      ],
    },
    // ---------- Diaphragm (copy of Level 5 diaphragm, s11- prefix) ----------
    {
      id: 'diaphragm',
      art: 'diaphragm',
      cols: 3,
      rows: 2,
      ports: [
        {
          id: 's11-diaphragm-top',
          kind: 'spout',
          facing: 'N',
          cellOffset: { dCol: 1, dRow: -1 },
          carries: ['pulse'],
          shape: 'castellated',
        },
      ],
    },
    // ---------- Brain (copy of Level 3 brain, s11- prefix + enriched-blood accept) ----------
    {
      id: 'brain',
      art: 'brain',
      cols: 3,
      rows: 3,
      activatesWhen: ['s11-brain-out'],
      ports: [
        {
          id: 's11-brain-in',
          kind: 'receptacle',
          facing: 'W',
          cellOffset: { dCol: -1, dRow: 1 },
          carries: ['food', 'oxygen'],
          // Level 11 specific: intestines side-out emits the enriched
          // [food, oxygen, co2] mix. Brain extracts the food+O2 from it;
          // real-body blood always carries CO2 too. Both mixes count as
          // CORRECT — either lets the level solve and the brain activate.
          acceptsAlso: [['food', 'oxygen', 'co2']],
        },
        {
          id: 's11-brain-out',
          kind: 'spout',
          facing: 'E',
          cellOffset: { dCol: 3, dRow: 1 },
          carries: ['co2'],
          dependsOn: ['s11-brain-in'],
        },
      ],
    },
    // ---------- Stomach (copy of Level 4/6 stomach, s11- prefix) ----------
    {
      id: 'stomach',
      art: 'stomach',
      cols: 3,
      rows: 3,
      activatesWhen: ['s11-stomach-out'],
      ports: [
        {
          id: 's11-stomach-in',
          kind: 'receptacle',
          facing: 'N',
          cellOffset: { dCol: 1, dRow: -1 },
          carries: ['gruel'],
        },
        {
          id: 's11-stomach-out',
          kind: 'spout',
          facing: 'S',
          cellOffset: { dCol: 1, dRow: 2 },
          carries: ['food'],
          shape: 'castellated',
          dependsOn: ['s11-stomach-in'],
        },
      ],
    },
    // ---------- Intestines (copy of Level 4/6 intestines, s11- prefix) ----------
    {
      id: 'intestines',
      art: 'intestines',
      cols: 4,
      rows: 3,
      activatesWhen: ['s11-int-left-out', 's11-int-bottom'],
      ports: [
        {
          id: 's11-int-top',
          kind: 'receptacle',
          facing: 'N',
          cellOffset: { dCol: 1, dRow: -1 },
          carries: ['food'],
          acceptsAny: true,
          shape: 'castellated',
        },
        {
          id: 's11-int-left-in',
          kind: 'receptacle',
          facing: 'W',
          cellOffset: { dCol: -1, dRow: 0 },
          carries: ['oxygen'],
          acceptsAny: true,
        },
        {
          id: 's11-int-left-out',
          kind: 'spout',
          facing: 'W',
          cellOffset: { dCol: -1, dRow: 1 },
          carries: ['food', 'oxygen', 'co2'],
          transform: (inputs) => {
            const top = inputs.get('s11-int-top') ?? [];
            const sideIn = inputs.get('s11-int-left-in') ?? [];
            const topHasFood = top.includes('food');
            if (sideIn.length === 0 && !topHasFood) return null;
            const out = new Set(sideIn);
            if (topHasFood) {
              out.add('food');
              out.add('co2');
            }
            return Array.from(out);
          },
        },
        {
          id: 's11-int-bottom',
          kind: 'spout',
          facing: 'S',
          cellOffset: { dCol: 2, dRow: 3 },
          carries: ['waste'],
          transform: (inputs) => {
            const top = inputs.get('s11-int-top') ?? [];
            return top.includes('food') ? ['waste'] : null;
          },
        },
      ],
    },
  ],
};
